import usePosOrderSelectedCartItemIndex from "@/shared/atoms/usePosOrderSelectedCartItemIndex";
import {
  useAddCartItem,
  useCart,
  useCartItemTotal,
  useDecreaseCartItem,
  useUpdateCartItemByIndex,
} from "@/shared/hooks/useCartHook";
import useCommon from "@/shared/hooks/useCommon";
import useConvertCartContentToCartHook from "@/shared/hooks/useConvertCartContentToCartHook";
import useIsOrderEditingHook from "@/shared/hooks/useIsOrderEditingHook";
import usePosSessionOrdersHook from "@/shared/hooks/usePosSessionOrdersHook";
import { getDeliveryBy, formatPrice } from "@/shared/utils/helpers";
import { useEffect, useRef, useState } from "react";
import Center from "../Base/Center";
import TextButton from "../Button/TextButton";
import Rotate from "../Table/Rotate";
import CartItem from "./CartItem";
import PosRow from "./PosRow";

const PosSessionCart = () => {
  const t = useCommon();
  const itemRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const [cart] = useCart();
  const orders = usePosSessionOrdersHook();
  const { isOrderEditing, setEditOrder } = useIsOrderEditingHook();
  const [shrunkenIds, setShrunkenIds] = useState([]);
  const [selectedCartItemIndex, setSelectedCartItemIndex] =
    usePosOrderSelectedCartItemIndex();
  const convertCartContentToCart = useConvertCartContentToCartHook();
  const getCartItemTotal = useCartItemTotal();
  const decreaseCartItem = useDecreaseCartItem();
  const addCartItem = useAddCartItem();
  const updateProductByIndex = useUpdateCartItemByIndex();

  const scrollToItem = (index: number) => {
    const itemRef = itemRefs.current[index];
    if (itemRef) {
      itemRef.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  };

  useEffect(() => {
    cart.length === 0 && setSelectedCartItemIndex(null);
  }, [cart, setSelectedCartItemIndex]);

  useEffect(() => {
    selectedCartItemIndex !== null && scrollToItem(selectedCartItemIndex);
  }, [selectedCartItemIndex, cart]);

  return (
    <div className="h-auto md:h-full overflow-y-auto rounded-[0.5rem] mb-2">
      {cart.map((cartItem, index) => (
        <CartItem
          key={cartItem.item_id}
          cartItem={cartItem}
          index={index}
          ref={(el) => (itemRefs.current[index] = el)}
          onSelect={() =>
            setSelectedCartItemIndex(
              selectedCartItemIndex === index ? null : index
            )
          }
          isSelected={selectedCartItemIndex === index}
          total={getCartItemTotal(cartItem)}
          onDelete={() => decreaseCartItem(cartItem)}
          onAdd={() => addCartItem({ ...cartItem, quantity: 1 })}
          onDeleteNote={() =>
            updateProductByIndex(index, { ...cartItem, note: "" })
          }
        />
      ))}

      {!isOrderEditing &&
        orders?.reverse()?.map((order, i) => {
          const cart = convertCartContentToCart(order.cart_content);
          const discount = parseFloat(
            order.order_information.subtotal_prices["2"] || "0"
          );
          const totalPrice = parseFloat(
            order.order_information.total_price || "0"
          );
          const remark = order.order_information.remarks;
          const orderId = order.order_information.item_id;
          const isShrunken = shrunkenIds.includes(orderId);

          return (
            <div
              key={orderId}
              onClick={() => {
                const newData = shrunkenIds.includes(orderId)
                  ? shrunkenIds.filter((id) => id !== orderId)
                  : [...shrunkenIds, orderId];

                setShrunkenIds(newData);
              }}
              className="cursor-pointer border-t-[1px_solid_var(--dark)]"
            >
              <div
                className="flex justify-between px-5 py-4 text-[1.9rem]"
                style={{ fontWeight: 900 }}
              >
                <div>
                  #{orders.length - i} -{" "}
                  {t(
                    getDeliveryBy(
                      order.order_information.delivery_method_id
                    ).toString()
                  )}
                </div>
                <Center gap="1rem">
                  <TextButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditOrder(order);
                    }}
                  >
                    {t("edit")}
                  </TextButton>
                  <Rotate
                    degrees={isShrunken ? "0deg" : "180deg"}
                    variants="chevron"
                    size="1.8rem"
                  />
                </Center>
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setEditOrder(order);
                }}
                style={{ display: isShrunken ? "none" : "block" }}
              >
                {cart.map((cartItem, index) => (
                  <CartItem
                    watchMode={true}
                    cartItem={cartItem}
                    index={index}
                    key={cartItem.item_id}
                    total={getCartItemTotal(cartItem)}
                  />
                ))}
                <div className="flex flex-col px-5 pt-4">
                  {discount !== 0 && (
                    <PosRow
                      fontWeight="semibold"
                      fontSize="sm"
                      label={t("posOrders.discount")}
                      color="error"
                    >
                      {formatPrice(discount)}
                    </PosRow>
                  )}
                  {remark && (
                    <PosRow
                      fontWeight="semibold"
                      fontSize="sm"
                      label={t("posOrders.remark")}
                      color="error"
                    >
                      {remark}
                    </PosRow>
                  )}

                  <PosRow label={t("total")}>{formatPrice(totalPrice)}</PosRow>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default PosSessionCart;
